// Free-booster-pack challenge catalogue (client-safe metadata only).
// Answers and database thresholds live in challenges.server.ts so the
// verification cannot be read from the browser bundle.
import { z } from "zod";

export type ChallengeKind = "social" | "quiz" | "stat";

export interface ChallengeDef {
  id: string;
  title: string;
  /** What the member has to do, in plain language. */
  detail: string;
  /** Booster packs awarded automatically on verification. */
  packs: 1 | 5 | 10;
  kind: ChallengeKind;
  /** Where the answer comes from on Pal Stars. */
  source?: string;
  /** Prompt shown above the answer box (quiz + social). */
  prompt?: string;
  placeholder?: string;
  /** Outbound link for social follows. */
  href?: string;
}

/**
 * Every challenge is one-shot: a unique (member, challenge) row is written when
 * it is awarded, so the same task can never be submitted for packs twice.
 */
export const CHALLENGES: ChallengeDef[] = [
  // ---- Easy · 1 free pack -------------------------------------------------
  {
    id: "follow-tiktok",
    title: "Follow Pal Stars on TikTok",
    detail: "Follow @palstars on TikTok, then drop your TikTok handle to claim your free pack.",
    packs: 1,
    kind: "social",
    prompt: "Your TikTok handle",
    placeholder: "@yourhandle",
    href: "https://www.tiktok.com/@palstars",
  },
  {
    id: "follow-instagram",
    title: "Follow Pal Stars on Instagram",
    detail: "Follow @palstars on Instagram, then drop your Instagram handle to claim your free pack.",
    packs: 1,
    kind: "social",
    prompt: "Your Instagram handle",
    placeholder: "@yourhandle",
    href: "https://www.instagram.com/palstars",
  },
  {
    id: "elements-quiz",
    title: "Element chart check",
    detail: "Which element is super effective against Dragon Pals? Answer from the Pal Stars element chart.",
    packs: 1,
    kind: "quiz",
    source: "/elements",
    prompt: "Element name",
    placeholder: "e.g. Water",
  },
  {
    id: "file-nine",
    title: "Fill your first binder page",
    detail: "File 9 cards into your collector binder. Verified from your binder automatically.",
    packs: 1,
    kind: "stat",
    source: "/binder",
  },

  // ---- Medium · 5 free packs ---------------------------------------------
  {
    id: "breed-rarest",
    title: "Breed the rarest Pal",
    detail:
      "Use the breeding chart to work out the rarest Pal you can hatch in Palworld — the fastest legendary flying mount. Name it to claim 5 packs.",
    packs: 5,
    kind: "quiz",
    source: "/breeding",
    prompt: "Rarest bred Pal",
    placeholder: "Pal name",
  },
  {
    id: "top-mount",
    title: "Name the fastest flying mount",
    detail: "Check the flying mount speed table and name the Pal with the highest sprint speed.",
    packs: 5,
    kind: "quiz",
    source: "/mounts",
    prompt: "Fastest flying mount",
    placeholder: "Pal name",
  },
  {
    id: "gem-mint",
    title: "Pull a Gem Mint 10",
    detail: "Land a flawless PalStars Grade 10 card. Verified from your collection automatically.",
    packs: 5,
    kind: "stat",
    source: "/pack-opening",
  },
  {
    id: "full-color-page",
    title: "Complete one color page",
    detail: "Fill all 9 pockets of any single binder page. Verified from your binder automatically.",
    packs: 5,
    kind: "stat",
    source: "/binder",
  },

  // ---- Hard · 10 free packs ---------------------------------------------
  {
    id: "chase-card",
    title: "Land a chase card",
    detail: "Pull an SSP, OSR or SSS Ultra Secret Gold card. Verified from your collection automatically.",
    packs: 10,
    kind: "stat",
    source: "/booster-pack",
  },
  {
    id: "open-fifty",
    title: "Open 50 packs",
    detail: "Bank 350 cards — that's 50 boosters. Verified from your collection automatically.",
    packs: 10,
    kind: "stat",
    source: "/pack-opening",
  },
  {
    id: "binder-complete",
    title: "Complete the 108-pocket binder",
    detail: "Fill every pocket across all 12 pages. Verified from your binder automatically.",
    packs: 10,
    kind: "stat",
    source: "/binder",
  },
];

export const CHALLENGE_BY_ID: Record<string, ChallengeDef> = Object.fromEntries(
  CHALLENGES.map((c) => [c.id, c]),
);

export const completeChallengeSchema = z.object({
  challenge_id: z.string().min(2).max(60),
  answer: z.string().trim().max(80).default(""),
});
